import { z } from 'zod';
import { decodeHtmlEntities } from '@utils/common';
import { configRefs } from '@utils/constants';
import { twentyThreeAudioSchema } from '../schema.twenty-three-audio.js';
import { bindInputMaker } from '../util.make-input.js';

const mediaInputBaseSchema = z.object({
  nodeType: z.literal("media-input").default("media-input"),
  correctAnswer: z.boolean().optional().default(false)
});
const audioMediaInputSchema = mediaInputBaseSchema.extend({
  type: z.literal("InlineAudio"),
  title: z.string().transform(it => decodeHtmlEntities(it)),
  src: z.string().nullish().default(null),
  twentyThreeAudio: twentyThreeAudioSchema
}).transform(({
  twentyThreeAudio,
  ...rest
}) => ({
  ...rest,
  src: twentyThreeAudio?.src || rest.src || ""
}));
const imageMediaInputSchema = mediaInputBaseSchema.extend({
  type: z.literal("Image"),
  title: z.string().transform(it => decodeHtmlEntities(it)),
  src: z.string(),
  audioSrc: z.string().nullable().default(null),
  altText: z.string().nullish().default(null),
  twentyThreeAudio: twentyThreeAudioSchema
}).transform(({
  twentyThreeAudio,
  ...rest
}) => ({
  ...rest,
  audioSrc: twentyThreeAudio?.src || rest.audioSrc || null
}));
const textMediaInputSchema = mediaInputBaseSchema.extend({
  type: z.literal("Text"),
  text: z.string().transform(it => decodeHtmlEntities(it)),
  audioSrc: z.string().nullable().default(null),
  twentyThreeAudio: twentyThreeAudioSchema
}).transform(({
  twentyThreeAudio,
  ...rest
}) => ({
  ...rest,
  audioSrc: twentyThreeAudio?.src || rest.audioSrc
}));
const videoMediaInputSchema = mediaInputBaseSchema.extend({
  type: z.literal("Video"),
  twentyThreeVideo: z.object({
    id: z.string(),
    token: z.string(),
    title: z.string(),
    originalWidth: z.string(),
    originalHeight: z.string(),
    standardThumbnail: z.string()
  }).transform(({
    originalHeight,
    originalWidth,
    standardThumbnail,
    ...data
  }) => {
    const height = Number(originalHeight);
    const width = Number(originalWidth);
    const aspectRatio = Number.isNaN(height) || Number.isNaN(width) ? null : `${height / width * 100}%`;
    return {
      ...data,
      height: Number.isNaN(height) ? null : height,
      width: Number.isNaN(width) ? null : width,
      aspectRatio,
      thumbnail: `${configRefs.apis.video23.video.dangerouslyAssertSyncValue()}${standardThumbnail}`
    };
  })
});
const selectAnswersMediaInputSchema = z.discriminatedUnion("type", [audioMediaInputSchema, imageMediaInputSchema, textMediaInputSchema, videoMediaInputSchema]);
const makeInput = bindInputMaker();
const checkboxMediaInput = makeInput({
  entry: {
    schema: z.object({
      type: z.literal("checkbox-media").default("checkbox-media"),
      markedAsCorrectAnswer: z.boolean().nullable().default(null)
    }),
    shouldValidate: (node, entry) => {
      return node.correctAnswer || !!entry?.markedAsCorrectAnswer;
    },
    validate: (node, entry) => {
      if (entry.markedAsCorrectAnswer === null) {
        return false;
      }
      return entry.markedAsCorrectAnswer === node.correctAnswer;
    },
    parseLegacyDetailItem: (item, context) => {
      if (context.relatedTask.taskType === "SelectAnswers" && ["Checkbox"].includes(context.relatedTask.answerType ?? "") && typeof item === "boolean") {
        return [context.relatedInput?.id ?? `${context.relatedTask.id}-${context.indexInDetails}`, {
          type: "checkbox-media",
          markedAsCorrectAnswer: item
        }];
      }
    }
  },
  schema: selectAnswersMediaInputSchema,
  postprocessSchemaInput: (input, context) => {
    return {
      ...input,
      id: `${context.taskId}-${context.index}`
    };
  }
});

export { checkboxMediaInput, selectAnswersMediaInputSchema };
//# sourceMappingURL=input.media.js.map
